import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  applyPrimaryTheme,
  getStoredCustomPrimaryColor,
  getStoredPrimaryThemeId,
  PRIMARY_THEME_PRESETS,
  type PrimaryThemeId,
  setStoredCustomPrimaryColor,
  setStoredPrimaryThemeId
} from '@/lib/theme/primary-theme';
import {
  applyFontTheme,
  FONT_THEME_OPTIONS,
  type FontThemeId,
  getStoredFontThemeId,
  setStoredFontThemeId
} from '@/lib/theme/font-theme';
import { useAuth } from '@/contexts/auth-context';
import { Entity } from '@/interfaces/auth';
import { toast } from '@/hooks/use-toast';
import api from '@/lib/api';

export function ProfileMenu({
  anchorEl,
  isOpen,
  onClose,
  onLogout
}: {
  anchorEl: HTMLElement | null;
  isOpen: boolean;
  onClose: () => void;
  onLogout: () => void;
}) {
  const { profile, entity } = useAuth();
  const menuRef = useRef<HTMLDivElement | null>(null);

  const [position, setPosition] = useState(() => {
    if (anchorEl) {
      const rect = anchorEl.getBoundingClientRect();
      return {
        right: window.innerWidth - rect.right,
        top: rect.bottom + 4
      };
    }
    return { right: 0, top: 0 };
  });

  const [selectedThemeId, setSelectedThemeId] = useState<PrimaryThemeId>('blue');
  const [customColor, setCustomColor] = useState('#023e8a');
  const [selectedFontId, setSelectedFontId] = useState<FontThemeId>('be-vietnam-pro');
  const [entities, setEntities] = useState<Entity[]>([]);
  const [selectedEntitySlug, setSelectedEntitySlug] = useState<string>('');
  const [isLoadingEntities, setIsLoadingEntities] = useState(false);
  const [isApplyingEntity, setIsApplyingEntity] = useState(false);

  useLayoutEffect(() => {
    if (!isOpen || !anchorEl) return;
    setSelectedThemeId(getStoredPrimaryThemeId());
    setCustomColor(getStoredCustomPrimaryColor());
    setSelectedFontId(getStoredFontThemeId());

    const updatePosition = () => {
      const rect = anchorEl.getBoundingClientRect();
      setPosition({
        right: window.innerWidth - rect.right,
        top: rect.bottom + 4
      });
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);

    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [anchorEl, isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const currentSlug = profile?.erp_active_entity_slug || profile?.entity_slug || entity?.slug || '';
    setSelectedEntitySlug(currentSlug);

    let isMounted = true;
    const fetchEntities = async () => {
      setIsLoadingEntities(true);
      try {
        const response = await api.get('/entities/');
        const data = response.data;
        const list: Entity[] = Array.isArray(data) ? data : data?.results || [];
        if (isMounted) {
          setEntities(list);
          if (!currentSlug && list.length > 0) {
            setSelectedEntitySlug(list[0].slug);
          }
        }
      } catch (error) {
        console.error('Failed to fetch entities:', error);
      } finally {
        if (isMounted) {
          setIsLoadingEntities(false);
        }
      }
    };

    fetchEntities();

    return () => {
      isMounted = false;
    };
  }, [isOpen, profile?.erp_active_entity_slug, profile?.entity_slug, entity?.slug]);

  const handleEntityChange = async (nextSlug: string) => {
    const currentSlug = profile?.erp_active_entity_slug || profile?.entity_slug || entity?.slug || '';
    if (!nextSlug || nextSlug === currentSlug || isApplyingEntity) return;

    setSelectedEntitySlug(nextSlug);
    setIsApplyingEntity(true);
    try {
      await api.post('/profile/active-entity/', {
        entity_slug: nextSlug
      });
      window.location.reload();
    } catch (error) {
      console.error('Failed to switch active entity:', error);
      setSelectedEntitySlug(currentSlug);
      toast({
        description: 'Không thể chuyển đơn vị làm việc. Vui lòng thử lại.',
        variant: 'destructive'
      });
      setIsApplyingEntity(false);
    }
  };

  const handleThemeSelect = (themeId: PrimaryThemeId) => {
    setSelectedThemeId(themeId);
    setStoredPrimaryThemeId(themeId);
    applyPrimaryTheme(themeId, customColor);
  };

  const handleCustomColorChange = (color: string) => {
    const nextColor = setStoredCustomPrimaryColor(color);

    setCustomColor(nextColor);
    setSelectedThemeId('custom');
    setStoredPrimaryThemeId('custom');
    applyPrimaryTheme('custom', nextColor);
  };

  const handleFontChange = (fontId: FontThemeId) => {
    setSelectedFontId(fontId);
    setStoredFontThemeId(fontId);
    applyFontTheme(fontId);
  };

  useEffect(() => {
    if (!isOpen) return;

    const handleMouseDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (menuRef.current?.contains(target) || anchorEl?.contains(target)) return;
      onClose();
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };

    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [anchorEl, isOpen, onClose]);

  if (!isOpen || !anchorEl) return null;

  return (
    <div
      ref={menuRef}
      aria-labelledby='profile-button'
      className='fixed z-50 w-60 min-w-[210px] rounded-sm border border-slate-200 bg-white p-1 text-xs shadow-sm'
      style={{
        right: position.right,
        top: position.top
      }}
    >
      <div className='border-b border-slate-100 px-2.5 py-2'>
        <div className='font-semibold text-slate-800'>
          {profile?.khai_bao_nguoi_su_dung_data?.nickname || profile?.nickname}
        </div>
        <div className='text-[10px] text-slate-400'>{profile?.user_ref}</div>
      </div>
      <button
        className='mt-1 block w-full rounded-sm px-2.5 py-1.5 text-left font-medium text-slate-700 transition-colors hover:bg-main/10 hover:text-main focus:outline-none'
        onClick={onClose}
        type='button'
      >
        Thông tin tài khoản
      </button>

      <div className='my-1 h-px bg-slate-100' />
      <div className='px-2.5 py-1.5'>
        <div className='mb-1.5 font-medium text-slate-700'>
          {isApplyingEntity ? 'Đang chuyển đơn vị...' : 'Đơn vị làm việc'}
        </div>
        <select
          aria-label='Chọn đơn vị làm việc'
          className='w-full rounded-sm border border-slate-200 bg-white px-2 py-1 text-slate-700 outline-none focus:border-main/60 focus:ring-1 focus:ring-main/60 disabled:bg-slate-50 disabled:text-slate-400'
          disabled={isLoadingEntities || isApplyingEntity}
          onChange={event => handleEntityChange(event.target.value)}
          value={selectedEntitySlug}
        >
          {isLoadingEntities && entities.length === 0 ? (
            <option value=''>Đang tải danh sách...</option>
          ) : entities.length === 0 ? (
            <option value=''>Không có dữ liệu</option>
          ) : (
            entities.map(item => (
              <option key={item.slug || item.uuid} value={item.slug}>
                {item.name || item.slug}
              </option>
            ))
          )}
        </select>
      </div>

      <div className='my-1 h-px bg-slate-100' />
      <div className='px-2.5 py-1.5'>
        <div className='mb-1.5 font-medium text-slate-700'>Màu chủ đạo</div>
        <div className='flex items-center gap-1.5'>
          {PRIMARY_THEME_PRESETS.map(preset => (
            <button
              key={preset.id}
              aria-label={`Đổi màu chủ đạo sang ${preset.label}`}
              aria-pressed={selectedThemeId === preset.id}
              className='size-5 rounded-full border border-slate-200 ring-offset-2 transition hover:scale-105 focus:outline-none focus:ring-2 focus:ring-main'
              onClick={() => handleThemeSelect(preset.id)}
              style={{ backgroundColor: `rgb(${preset.main})` }}
              title={preset.label}
              type='button'
            >
              <span
                className={`block size-full rounded-full ${
                  selectedThemeId === preset.id ? 'ring-2 ring-main ring-offset-2' : ''
                }`}
              />
            </button>
          ))}
        </div>
        <label className='mt-2 flex cursor-pointer items-center justify-between gap-2 rounded-sm border border-slate-100 px-2 py-1.5 text-slate-700 transition-colors hover:border-main/40 hover:bg-main/5'>
          <span className='font-medium'>Tùy chọn</span>
          <span className='flex items-center gap-1.5'>
            <span className='font-mono text-[11px] uppercase text-slate-500'>{customColor}</span>
            <input
              aria-label='Tự chọn màu chủ đạo'
              className='h-6 w-7 cursor-pointer rounded border-0 bg-transparent p-0'
              onChange={event => handleCustomColorChange(event.target.value)}
              type='color'
              value={customColor}
            />
          </span>
        </label>
      </div>
      <div className='my-1 h-px bg-slate-100' />
      <div className='px-2.5 py-1.5'>
        <div className='mb-1.5 font-medium text-slate-700'>Font chữ</div>
        <select
          aria-label='Chọn font chữ'
          className='w-full rounded-sm border border-slate-200 bg-white px-2 py-1 text-slate-700 outline-none focus:border-main/60 focus:ring-1 focus:ring-main/60'
          onChange={event => handleFontChange(event.target.value as FontThemeId)}
          value={selectedFontId}
        >
          {FONT_THEME_OPTIONS.map(option => (
            <option key={option.id} value={option.id}>
              {option.label}
            </option>
          ))}
        </select>
      </div>
      <div className='my-1 h-px bg-slate-100' />
      <button
        className='block w-full rounded-sm px-2.5 py-1.5 text-left font-medium text-red-600 transition-colors hover:bg-red-50 focus:outline-none'
        onClick={onLogout}
        type='button'
      >
        Đăng xuất
      </button>
    </div>
  );
}
